import { Check, Pencil, Plus, Trash2, X } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { ROOM_NAME_MAX, type RoomView } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Input } from '../../../../components/Input';
import { VentureLogo } from '../../../../components/VentureLogo';
import { formatNumber } from '../../../../lib/format';
import { isPendingRoom } from '../../../interviews/api/interviews-api';
import type { RoomActions } from './use-room-actions';
import styles from './VentureColumn.module.css';

export interface RoomVenture {
  readonly id: string;
  readonly name: string;
  readonly logoVersion: string | null;
}

const NAME_TAKEN = 'This company already has a room with that name.';

function isNameTaken(rooms: readonly RoomView[], name: string, exceptId: string | null): boolean {
  const wanted = name.trim().toLowerCase();
  return rooms.some((room) => room.id !== exceptId && room.name.trim().toLowerCase() === wanted);
}

interface VentureColumnProps {
  readonly venture: RoomVenture;
  /** Every room of the venture, for the name check. */
  readonly rooms: readonly RoomView[];
  /** The ones the search lets through. */
  readonly shown: readonly RoomView[];
  readonly isSearching: boolean;
  readonly actions: RoomActions;
}

/**
 * Rooms A: one column per venture (`5-settings/rooms.html`), its rooms as cards and the add row at the
 * bottom, so the venture is never picked from a list. A venture with no rooms still has its column.
 */
export function VentureColumn({ venture, rooms, shown, isSearching, actions }: VentureColumnProps): ReactNode {
  return (
    <section className={styles.column} aria-label={`Rooms at ${venture.name}`}>
      <header className={styles.head}>
        <VentureLogo venture={venture} />
        <h2 className={styles.title}>{venture.name}</h2>
        <span className={styles.count} aria-label={`${rooms.length} ${rooms.length === 1 ? 'room' : 'rooms'}`}>
          {formatNumber(rooms.length)}
        </span>
      </header>
      {shown.length > 0 ? (
        <ul className={styles.list}>
          {shown.map((room) => (
            <RoomCard key={room.id} room={room} rooms={rooms} actions={actions} />
          ))}
        </ul>
      ) : (
        <p className={styles.none}>{isSearching ? 'No room here matches.' : 'No rooms yet.'}</p>
      )}
      {!isSearching && <AddRoomRow venture={venture} rooms={rooms} actions={actions} />}
    </section>
  );
}

function AddRoomRow({ venture, rooms, actions }: { readonly venture: RoomVenture; readonly rooms: readonly RoomView[]; readonly actions: RoomActions }): ReactNode {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const add = (): void => {
    const typed = name.trim();
    if (typed === '') return;
    if (isNameTaken(rooms, typed, null)) {
      setError(NAME_TAKEN);
      return;
    }
    // Clears at once; a refusal puts the name back with the reason under it (06-qol §4).
    actions.add(venture, typed, (message) => {
      setName(typed);
      setError(message);
    });
    setName('');
  };

  return (
    <div className={styles.add}>
      <Input
        icon={Plus}
        aria-label={`Add a room at ${venture.name}`}
        placeholder="Add a room, press Enter"
        maxLength={ROOM_NAME_MAX}
        value={name}
        isInvalid={error !== null}
        onChange={(event) => {
          setName(event.target.value);
          setError(null);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            add();
          }
        }}
      />
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * One room. Rename happens in the card: Enter saves, Esc keeps the old name. While interviews are booked
 * ahead the bin is off and the card says why in its own words — no pop-up covering the next room
 * (critique, Rooms A).
 */
function RoomCard({ room, rooms, actions }: { readonly room: RoomView; readonly rooms: readonly RoomView[]; readonly actions: RoomActions }): ReactNode {
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The new name from Enter until the list has it: the list updates a tick later, and the old name must
  // not flash back in between (§5a, "the one test"). Dropped once the list agrees, or on a refusal.
  const [savedName, setSavedName] = useState<string | null>(null);
  if (savedName !== null && room.name === savedName) setSavedName(null);
  const shownName = savedName ?? room.name;
  const isPending = isPendingRoom(room);
  const booked = room.upcomingInterviews;

  const save = (): void => {
    if (draft === null) return;
    const typed = draft.trim();
    if (typed === '' || typed === room.name) {
      setDraft(null);
      setError(null);
      return;
    }
    if (isNameTaken(rooms, typed, room.id)) {
      setError(NAME_TAKEN);
      return;
    }
    actions.rename(room, typed, (message) => {
      setSavedName(null);
      setDraft(typed);
      setError(message);
    });
    setSavedName(typed);
    setDraft(null);
    setError(null);
  };

  const cancel = (): void => {
    setDraft(null);
    setError(null);
  };

  return (
    <li className={styles.card} data-editing={draft !== null || undefined}>
      {draft === null ? (
        <div className={styles.cardTop}>
          <span className={styles.name}>{shownName}</span>
          <span className={styles.actions}>
            <Button variant="ghost" size="sm" icon={Pencil} aria-label={`Rename ${room.name}`} disabled={isPending} onClick={() => setDraft(room.name)} />
            <Button
              variant="ghost"
              size="sm"
              icon={Trash2}
              aria-label={booked > 0 ? `${room.name} can't be taken off while interviews are booked or offered in it` : `Take ${room.name} off the list`}
              disabled={isPending || booked > 0}
              onClick={() => actions.takeOff(room)}
            />
          </span>
        </div>
      ) : (
        <form
          className={styles.cardTop}
          onSubmit={(event) => {
            event.preventDefault();
            save();
          }}
        >
          <Input
            aria-label={`New name for ${room.name}`}
            autoFocus
            maxLength={ROOM_NAME_MAX}
            value={draft}
            isInvalid={error !== null}
            onChange={(event) => {
              setDraft(event.target.value);
              setError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.preventDefault();
                cancel();
              }
            }}
          />
          <span className={styles.actions}>
            <Button type="submit" variant="solid" size="sm" icon={Check} aria-label="Save the name" disabled={draft.trim() === ''} />
            <Button type="button" variant="ghost" size="sm" icon={X} aria-label="Keep the old name" onClick={cancel} />
          </span>
        </form>
      )}
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      <span className={styles.meta}>
        {booked === 0 ? 'Nothing booked ahead' : `${formatNumber(booked)} interview${booked === 1 ? '' : 's'} booked or offered ahead`}
      </span>
      {booked > 0 && (
        <span className={styles.reason}>
          Move {booked === 1 ? 'its interview' : `its ${formatNumber(booked)} interviews`} to another room first, then it can be taken off.
        </span>
      )}
    </li>
  );
}
